import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ContentHeaderAction, ContentHeaderTitle } from 'app/key-ui/content-header/content-header.component';
import { TopNavMobileNavigationAction, TopNavSettings } from 'app/key-ui/top-nav';
import { MatchMediaService } from 'app/services';
import { KeyLayoutViewModule } from '../layout-view/layout-view.module';

// The route region of a document-centric screen: the application's header band, and the rest of the
// region for whatever the screen projects.
//
// It exists because the application draws the button that reopens the main navigation inside each
// page's own header band rather than in the shell — kui-top-nav hides itself once the sidebar goes
// off-canvas — so a screen without that band leaves a phone with no way back to the menu.
@Component({
    selector: 'key-layout-doc',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [KeyLayoutViewModule],
    template: `
        <key-layout-view [show]="true" [header]="header()">
            <div class="content"><ng-content /></div>
        </key-layout-view>
    `,
    styles: `
        // The route region is a flex column, and this fills it. A screen mounting this takes
        // display: contents so that this element is the one the region measures.
        :host {
            display: flex;
            flex-direction: column;
            flex: 1 1 0;
            min-height: 0;
        }

        // What the band leaves. It is here rather than on the projected element because a
        // component's styles do not reach projected content, and it is a grid so that whatever the
        // screen puts in it is stretched to the region's size without being given a rule of its
        // own — which is the definite height a rad-page needs to own its scrolling.
        //
        // Both tracks are minmax(0, 1fr) rather than the 1fr they would otherwise be. A track's
        // automatic minimum is its content's min-content size, so a wide table inside would become a
        // floor the track could not shrink below and the region would overflow instead of scrolling.
        .content {
            display: grid;
            grid-template-columns: minmax(0, 1fr);
            grid-template-rows: minmax(0, 1fr);
            flex: 1 1 0;
            min-height: 0;
        }
    `,
})
export class KeyLayoutDocComponent {

    /** What the band names the screen. Drawn only where the band is drawn. */
    readonly title = input<string | ContentHeaderTitle>();

    /** The controls at the band's trailing edge. */
    readonly actions = input<ContentHeaderAction[]>();

    /**
     * What the band's leading control does. Left unset it is the menu icon wired to the sidebar
     * toggle, which is what a screen reached from the main navigation wants; a screen reached from
     * another screen sets `back` or `close` and its own handler.
     */
    readonly navigationAction = input<TopNavMobileNavigationAction>(null);

    // Whether the main navigation has collapsed into the off-canvas sidebar. It is the shell's own
    // test — app.component derives showSidebar from isMobile || isTablet — rather than a breakpoint
    // of this component's own, because the band exists to reopen that sidebar and the two must agree
    // on where it goes.
    private readonly navigationCollapsed = toSignal(inject(MatchMediaService).isMobileOrTablet);

    // No band where there is nothing to put in it: a desktop keeps its main navigation, so a screen
    // that passes neither a title nor an action gets its full height back.
    protected readonly header = computed<TopNavSettings>(() => {
        if (!this.navigationCollapsed() && !this.title() && !this.actions()?.length) {
            return null;
        }
        return {
            title: this.title(),
            actions: this.actions() ?? [],
            navigationAction: this.navigationAction(),
        };
    });
}
